import { useMemo, useState } from "react";
import { DateTime } from "luxon";

import { useWidget } from "/imports/state/WidgetContext";
import { fullName } from "/imports/ui/components/discovery";
import { MENU_CATEGORY_TYPES, SLOT_STEP_MIN, DAY_KEYS, minutesToValue, minutesToLabel, timeRangesToState, collectPeriods } from "./timeUtils";

/**
 * Holds the full preferences form state and exposes a `buildPayload()` that
 * serializes it into the API shape ({ sports, services, trainers, timeRanges,
 * birthDate }) or a validation error. `initial` (a saved `preferences` sub-doc
 * plus `birthDate`) hydrates the form for editing; omit it for a blank signup
 * form. Options come from the widget init payload via WidgetContext.
 */
export default function usePreferencesForm(initial) {
  const { services, trainers, sports, locations } = useWidget();

  const menuServices = useMemo(() => (services || []).filter((s) => MENU_CATEGORY_TYPES.includes(s.categoryType)), [services]);
  const serviceOptions = useMemo(() => menuServices.map((s) => ({ serviceId: s._id, serviceName: s.name })), [menuServices]);

  const sportOptions = useMemo(() => {
    const ids = new Set(menuServices.map((s) => s.sportId).filter(Boolean));
    return (sports || []).filter((s) => ids.has(s._id)).map((s) => ({ sportId: s._id, sportName: s.name }));
  }, [menuServices, sports]);
  const trainerOptions = useMemo(
    () => (trainers || []).filter((t) => t.isActive !== false).map((t) => ({ trainerId: t._id, trainerName: fullName(t) })),
    [trainers]
  );
  const locationOptions = useMemo(() => (locations || []).map((l) => ({ locationId: l._id, locationName: l.name })), [locations]);

  // Distinct durations (minutes) the club offers across bookable services, read
  // straight from their pricing — the set the client can prefer.
  const durationOptions = useMemo(() => {
    const set = new Set();
    (menuServices || []).forEach((s) => {
      Object.values(s.pricing || {}).forEach((opt) => {
        const d = parseInt(opt?.base?.duration, 10);
        if (Number.isInteger(d) && d > 0) set.add(d);
      });
    });
    return [...set].sort((a, b) => a - b);
  }, [menuServices]);

  // Offer only times the club is open: the widest window across all locations'
  // opening hours (stored as minutes from midnight). Falls back to a full day.
  const openWindow = useMemo(() => {
    let earliest = Infinity;
    let latest = -Infinity;
    (locations || []).forEach((loc) => {
      Object.values(loc.openingHours || {}).forEach((day) => {
        if (!day || !day.isOn) return;
        (day.shifts || []).forEach((shift) => {
          const from = parseInt(shift.from, 10);
          const to = parseInt(shift.to, 10);
          if (Number.isFinite(from)) earliest = Math.min(earliest, from);
          if (Number.isFinite(to)) latest = Math.max(latest, to);
        });
      });
    });
    if (!Number.isFinite(earliest) || !Number.isFinite(latest)) return { from: 0, to: 24 * 60 - SLOT_STEP_MIN, bounded: false };
    return { from: earliest, to: latest, bounded: true };
  }, [locations]);

  const slots = useMemo(() => {
    const out = [];
    for (let m = openWindow.from; m <= openWindow.to; m += SLOT_STEP_MIN) {
      out.push({ value: minutesToValue(m), label: minutesToLabel(m) });
    }
    return out;
  }, [openWindow]);

  const seededTime = useMemo(() => timeRangesToState(initial?.timeRanges), [initial]);
  // Saved sport/service/trainer entries already match the option shape, so the
  // stored objects double as autocomplete values; freshly-derived options keep
  // labels current and a since-removed entry still renders from its stored name.
  const seededSports = useMemo(() => {
    const saved = Array.isArray(initial?.sports) ? initial.sports : [];
    return saved.map((s) => sportOptions.find((o) => o.sportId === s.sportId) || s).filter((s) => s && s.sportId);
  }, [initial, sportOptions]);
  const seededServices = useMemo(() => {
    const saved = Array.isArray(initial?.services) ? initial.services : [];
    return saved.map((s) => serviceOptions.find((o) => o.serviceId === s.serviceId) || s).filter((s) => s && s.serviceId);
  }, [initial, serviceOptions]);
  const seededTrainers = useMemo(() => {
    const saved = Array.isArray(initial?.trainers) ? initial.trainers : [];
    return saved.map((t) => trainerOptions.find((o) => o.trainerId === t.trainerId) || t).filter((t) => t && t.trainerId);
  }, [initial, trainerOptions]);
  const seededLocations = useMemo(() => {
    const saved = Array.isArray(initial?.locations) ? initial.locations : [];
    return saved.map((l) => locationOptions.find((o) => o.locationId === l.locationId) || l).filter((l) => l && l.locationId);
  }, [initial, locationOptions]);
  const seededDurations = useMemo(() => {
    const saved = Array.isArray(initial?.durations) ? initial.durations : [];
    return saved.map((d) => parseInt(d, 10)).filter((n) => Number.isInteger(n) && n > 0);
  }, [initial]);
  const seededBirthDate = useMemo(() => {
    const d = initial?.birthDate ? DateTime.fromISO(initial.birthDate) : null;
    return d && d.isValid ? d : null;
  }, [initial]);

  const [selectedSports, setSelectedSports] = useState(seededSports);
  const [selectedServices, setSelectedServices] = useState(seededServices);
  const [selectedTrainers, setSelectedTrainers] = useState(seededTrainers);
  const [selectedLocations, setSelectedLocations] = useState(seededLocations);
  const [selectedDurations, setSelectedDurations] = useState(seededDurations);
  const [weekdayPeriods, setWeekdayPeriods] = useState(seededTime.weekdayPeriods);
  const [weekendPeriods, setWeekendPeriods] = useState(seededTime.weekendPeriods);
  const [perDayPeriods, setPerDayPeriods] = useState(seededTime.perDayPeriods);
  const [useAdvanced, setUseAdvanced] = useState(seededTime.useAdvanced);
  const [birthDate, setBirthDate] = useState(seededBirthDate);

  // Both time editors live in a modal. `timeDialog` picks which one ("perday" |
  // "scope") and `timeDraft` holds its edits so Cancel discards them. Applying a
  // modal commits its values AND makes that mode the one buildPayload serializes.
  const [timeDialog, setTimeDialog] = useState(null);
  const [timeDraft, setTimeDraft] = useState(null);

  const openPerDayDialog = () => {
    setTimeDraft(perDayPeriods);
    setTimeDialog("perday");
  };
  const openScopeDialog = () => {
    setTimeDraft({ weekday: weekdayPeriods, weekend: weekendPeriods });
    setTimeDialog("scope");
  };
  const closeTimeDialog = () => setTimeDialog(null);
  const setDraftDay = (day, periods) => setTimeDraft((prev) => ({ ...prev, [day]: periods }));
  const setDraftScope = (scope, periods) => setTimeDraft((prev) => ({ ...prev, [scope]: periods }));
  const applyTimeDialog = () => {
    if (timeDialog === "perday") {
      setPerDayPeriods(timeDraft);
      setUseAdvanced(true);
    } else {
      setWeekdayPeriods(timeDraft.weekday);
      setWeekendPeriods(timeDraft.weekend);
      setUseAdvanced(false);
    }
    setTimeDialog(null);
  };

  const buildPayload = () => {
    const timeRanges = [];
    if (useAdvanced) {
      for (const day of DAY_KEYS) {
        const ranges = collectPeriods(perDayPeriods[day] || [], day);
        if (ranges === null) return { error: "Please choose both a start and an end time for each period, or remove it." };
        timeRanges.push(...ranges);
      }
    } else {
      const weekdays = collectPeriods(weekdayPeriods, "weekdays");
      const weekend = collectPeriods(weekendPeriods, "weekend");
      if (weekdays === null || weekend === null) {
        return { error: "Please choose both a start and an end time for each period, or remove it." };
      }
      timeRanges.push(...weekdays, ...weekend);
    }

    // If the client didn't skip and picked ANYTHING — a time, a trainer, a
    // duration, anything — require the fields that keep "Suggested for you" fast
    // and focused: at least one service, one sport, one location. Without them
    // the feed falls back to every service at every location. A fully empty form
    // is a skip and passes through. Each requirement is gated on the club
    // actually offering that option, so no-sports / single-location never blocks.
    const hasAnyPref =
      selectedSports.length > 0 ||
      selectedServices.length > 0 ||
      selectedTrainers.length > 0 ||
      selectedLocations.length > 0 ||
      selectedDurations.length > 0 ||
      timeRanges.length > 0;

    if (hasAnyPref) {
      const missing = [];
      if (serviceOptions.length > 0 && selectedServices.length === 0) missing.push("service");
      if (sportOptions.length > 0 && selectedSports.length === 0) missing.push("sport");
      if (locationOptions.length > 0 && selectedLocations.length === 0) missing.push("location");
      if (missing.length > 0) {
        const label = missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
        return { error: `To tailor your suggestions, please also add at least one ${label} — or clear your selections to skip.` };
      }
    }

    return {
      payload: {
        sports: selectedSports,
        services: selectedServices,
        trainers: selectedTrainers,
        locations: selectedLocations,
        timeRanges,
        durations: selectedDurations,
        birthDate: birthDate && birthDate.isValid ? birthDate.toFormat("yyyy-MM-dd") : ""
      }
    };
  };

  return {
    sportOptions,
    serviceOptions,
    trainerOptions,
    locationOptions,
    durationOptions,
    slots,
    openWindow,
    selectedSports,
    setSelectedSports,
    selectedServices,
    setSelectedServices,
    selectedTrainers,
    setSelectedTrainers,
    selectedLocations,
    setSelectedLocations,
    selectedDurations,
    setSelectedDurations,
    weekdayPeriods,
    weekendPeriods,
    perDayPeriods,
    useAdvanced,
    timeDialog,
    timeDraft,
    openPerDayDialog,
    openScopeDialog,
    closeTimeDialog,
    setDraftDay,
    setDraftScope,
    applyTimeDialog,
    birthDate,
    setBirthDate,
    buildPayload
  };
}
